import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    topic: '',
    location: '',
    inquiry: '',
    optIn: false,
    hasBooking: '',
    bookingNumber: '',
};

// Custom validation for Location based on Topic
const validationSchema = Yup.object().shape({
    firstName: Yup.string()
        .required('Please enter your First Name.')
        .min(1, 'Please enter your First Name.'),
    lastName: Yup.string()
        .required('Please enter your Last Name.')
        .min(1, 'Please enter your Last Name.'),
    email: Yup.string()
        .required('Please enter your Email Address.')
        .email('Please enter a valid Email Address.'),
    mobile: Yup.string()
        .matches(/^\d*$/, 'Phone Number must be numeric.')
        .max(20, 'Phone Number cannot exceed 20 digits.'),
    topic: Yup.string()
        .required('Please select a Topic.')
        .min(1, 'Please select a Topic.'),
    location: Yup.string().when('topic', {
        is: (val) => val === 'Shuttle Service',
        then: () => Yup.string().required('Please select a Location.'),
        otherwise: () => Yup.string(),
    }),
    hasBooking: Yup.string(), // Not required
    bookingNumber: Yup.string(), // Not required
    inquiry: Yup.string()
        .required('Please enter your Inquiry.')
        .min(10, 'At Least 10 characters are required for your Inquiry.'),
});

const locations = [
    { text: 'Location', value: '' },
    { text: 'Calgary', value: 'Calgary' },
    { text: 'Edmonton', value: 'Edmonton' },
    { text: 'Halifax', value: 'Halifax' },
    { text: 'Toronto', value: 'Toronto' },
    { text: 'Vancouver', value: 'Vancouver' },
    { text: 'Whitehorse', value: 'Whitehorse' }
];

const topis = [
    { text: 'Topic*', value: '' },
    { text: 'General Inquiry', value: 'General Inquiry' },
    { text: 'Existing Booking', value: 'Existing Booking' },
    { text: 'OnRoad Support', value: 'OnRoad Support' },
    { text: 'Shuttle Service', value: 'Shuttle Service' }
];

const ContactUsForm = ({ onSubmit, disabled, errorMsg }) => {
    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (formData) => onSubmit({ ...formData })
    });

    const getErrorMsg = (name) => {
        return formik.touched[name] && formik.errors[name] ? (
            <div style={{ color: 'red' }}>{formik.errors[name]}</div>
        ) : null;
    };

    const getFormGroupClass = (fieldName, override) => {
        const defaultClassName = override || 'form-group';
        return formik.touched[fieldName] && formik.errors[fieldName] ? `${defaultClassName} invalid` : `${defaultClassName}`;
    };

    return (
        <div className='container my-4'>
            <h3 className='mb-4'>Contact Us Today</h3>
            {errorMsg ? <p className='alert alert-danger'>{errorMsg}</p> : null}
            <form onSubmit={formik.handleSubmit}>
                <div className="form-row">
                    <div className={getFormGroupClass('firstName') + " col-12 col-md-6"}>
                        <input
                            className="form-control"
                            id="firstName"
                            name="firstName"
                            placeholder="First Name*"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.firstName}
                        />
                        {getErrorMsg('firstName')}
                    </div>

                    <div className={getFormGroupClass('lastName') + " col-12 col-md-6"}>
                        <input
                            className="form-control"
                            id="lastName"
                            name="lastName"
                            placeholder="Last Name*"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.lastName}
                        />
                        {getErrorMsg('lastName')}
                    </div>

                    <div className={getFormGroupClass('email') + " col-12"}>
                        <input
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="Email*"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                        />
                        {getErrorMsg('email')}
                    </div>

                    <div className={getFormGroupClass('mobile') + " col-12"}>
                        <input
                            className="form-control"
                            id="mobile"
                            name="mobile"
                            placeholder="Phone Number"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.mobile}
                            maxLength={20}
                        />
                        {getErrorMsg('mobile')}
                    </div>

                    <div className={getFormGroupClass('topic') + " col-12"}>
                        <select
                            className="form-control"
                            id="topic"
                            name="topic"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.topic}
                        >
                            {topis.map(({ text, value }, index) => (
                                <option key={index} value={value}>
                                    {text}
                                </option>
                            ))}
                        </select>
                        {getErrorMsg('topic')}
                    </div>

                    <div className={getFormGroupClass('location') + " col-12"}>
                        <select
                            className="form-control"
                            id="location"
                            name="location"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.location}
                        >
                            {locations.map(({ text, value }, index) => (
                                <option key={index} value={value}>
                                    {text}
                                </option>
                            ))}
                        </select>
                        {getErrorMsg('location')}
                    </div>

                    {/* Confirmed Booking field: not required */}
                    <div className={getFormGroupClass('hasBooking') + " col-12"}>
                        <label>Do you have a quote or booking number?</label>
                        <div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="hasBookingYes"
                                    name="hasBooking"
                                    value="yes"
                                    checked={formik.values.hasBooking === 'yes'}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <label className="form-check-label" htmlFor="hasBookingYes">
                                    Yes
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="hasBookingNo"
                                    name="hasBooking"
                                    value="no"
                                    checked={formik.values.hasBooking === 'no'}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <label className="form-check-label" htmlFor="hasBookingNo">
                                    No
                                </label>
                            </div>
                        </div>
                        {getErrorMsg('hasBooking')}
                    </div>

                    {/* Conditionally show Booking/Quote Number */}
                    {formik.values.hasBooking === 'yes' && (
                        <div className={getFormGroupClass('bookingNumber') + " col-12"}>
                            <input
                                className="form-control"
                                id="bookingNumber"
                                name="bookingNumber"
                                placeholder="Your Booking/Quote Number"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.bookingNumber}
                            />
                            {getErrorMsg('bookingNumber')}
                        </div>
                    )}

                    <div className={getFormGroupClass('inquiry') + " col-12"}>
                        <textarea
                            className="form-control"
                            id="inquiry"
                            name="inquiry"
                            placeholder="Inquiry"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.inquiry}
                        />
                        {getErrorMsg('inquiry')}
                    </div>

                    <div className='form-group'>
                        <div className='form-check'>
                            <input
                                type='checkbox'
                                className="form-check-input "
                                id="optIn"
                                name="optIn"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value='optIn'
                            />
                            <label className="form-check-label ml-4" htmlFor="optIn">
                                Yes, I consent to receive promotional emails from Fraserway RV. I understand that I can unsubscribe at any time.
                            </label>
                            {getErrorMsg('optIn')}
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={disabled} className='btn btn-primary'>Submit</button>
            </form>
        </div>
    );
};

export default ContactUsForm;
